import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Copy, Loader2 } from "lucide-react";

export default function ConsoleDuplicateCleanup() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/console/requirements/duplicates").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/console/requirements/merge-duplicates");
      setResult(data);
      toast.success(data.removed ? `Merged ${data.groups} duplicate group(s), removed ${data.removed} copy(ies)` : "No duplicates found");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <section className="border border-border p-5 space-y-3" data-testid="duplicate-cleanup-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Copy className="h-4 w-4" /> Duplicate Requirement Cleanup</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Finds requirements that appear more than once on the same standard (e.g. "R1" and "r1"), keeps the most complete copy,
            and moves every entity's SME / compliance owners, legal holds and linked records onto it.
          </p>
          <p className="text-sm mt-2" data-testid="duplicate-cleanup-count">
            {info == null ? "Checking…" : info.groups === 0 ? "No duplicates found."
              : `${info.groups} duplicate group(s) · ${info.extra_copies} extra copy(ies)`}
          </p>
        </div>
        <Button onClick={merge} disabled={busy || !info?.groups} className="rounded-none" data-testid="duplicate-cleanup-merge-btn">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Find &amp; merge duplicates
        </Button>
      </div>
      {result?.items?.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-1" data-testid="duplicate-cleanup-result">
          {result.items.map((m) => (
            <li key={m.kept_id}>{m.standard_id} {m.req_number}: kept 1, removed {m.removed}</li>
          ))}
          <li>{result.references_moved} linked record(s) repointed.</li>
        </ul>
      )}
    </section>
  );
}
